import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { hasAccess } from '../lib/rbac.js';

const HEARTBEAT_MS = 60_000;
const MAX_AVATARS = 4;

// Paleta de gradientes via tokens do design system
const PALETTE = [
  ['var(--color-ft-blue)', 'var(--color-fc-lilas)'],
  ['var(--accent)', 'var(--color-ft-dark)'],
  ['var(--success)', 'var(--color-ft-blue)'],
  ['var(--warning)', 'var(--color-fc-orange)'],
  ['var(--danger)', 'var(--color-fc-orange-dark)'],
  ['var(--color-fc-lilas)', 'var(--accent)'],
  ['var(--brand)', 'var(--color-fc-navy)'],
  ['var(--color-fc-gold)', 'var(--warning)'],
  ['var(--accent-hover)', 'var(--color-ft-blue)'],
  ['var(--color-fc-orange)', 'var(--danger)'],
];

function nameHash(name) {
  return (name || 'U').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
}

function avatarGradient(name) {
  const [from, to] = PALETTE[nameHash(name) % PALETTE.length];
  return `linear-gradient(135deg, ${from}, ${to})`;
}

// Ring shadow adaptado ao tema do topbar
function avatarShadow(theme) {
  return theme === 'light'
    ? '0 0 0 2.5px rgba(255,255,255,0.9), 0 0 0 4px rgba(69,91,216,0.2), 0 2px 6px rgba(0,0,0,0.12)'
    : '0 0 0 2.5px rgba(6,13,38,0.9), 0 0 0 4px rgba(255,255,255,0.15), 0 2px 6px rgba(0,0,0,0.5)';
}

function extrasShadow(theme) {
  return theme === 'light'
    ? '0 0 0 2.5px rgba(255,255,255,0.9), 0 0 0 4px rgba(69,91,216,0.15)'
    : '0 0 0 2.5px rgba(6,13,38,0.9), 0 0 0 4px rgba(255,255,255,0.1)';
}

export function OnlineUsers({ theme = 'dark' }) {
  const { level } = useAuth();
  const canMonitor = hasAccess(level, 'admin');
  const [online, setOnline] = useState([]);
  const [hoverId, setHoverId] = useState(null);

  useEffect(() => {
    let active = true;
    const beat = async () => {
      try {
        const res = await api.presencaHeartbeat();
        if (active) setOnline(res?.data?.online || []);
      } catch {
        // silencioso — próximo heartbeat tenta de novo
      }
    };
    beat();
    const t = window.setInterval(beat, HEARTBEAT_MS);
    return () => { active = false; window.clearInterval(t); };
  }, []);

  if (online.length === 0) return null;

  const visiveis = online.slice(0, MAX_AVATARS);
  const extras = online.length - visiveis.length;

  return (
    <div className="hidden sm:flex items-center gap-3">
      {canMonitor && (
      <Link 
        to="/sistemas/monitoramento" 
        className="text-xs font-600 text-accent hover:underline hidden md:block mr-1"
        title="Ver todos os usuários online"
      >
        Ver todos
      </Link>
      )}
      <div
        className="flex items-center gap-2 cursor-pointer"
        title={`Online agora: ${online.map((u) => u.nome).join(', ')}`}
      >

      <div className="flex -space-x-2">
        {visiveis.map((u) => (
          <div
            key={u.id}
            className="relative w-8 h-8 rounded-full overflow-visible cursor-default select-none"
            onMouseEnter={() => setHoverId(u.id)}
            onMouseLeave={() => setHoverId(null)}
            aria-label={`${u.nome} online`}
          >
            {/* Avatar */}
            <div
              className="w-full h-full rounded-full overflow-hidden grid place-items-center text-white text-[12px] font-700 transition-transform duration-150 hover:scale-110 hover:z-10 relative"
              style={{
                background: u.foto_url ? undefined : avatarGradient(u.nome),
                boxShadow: avatarShadow(theme),
              }}
            >
              {u.foto_url ? (
                <img src={u.foto_url} alt={u.nome} className="w-full h-full object-cover" />
              ) : (
                (u.nome || '?').charAt(0).toUpperCase()
              )}

              {/* Dot de presença com glow pulsante */}
              <span
                className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full bg-[color:var(--success)] border-2 online-dot-pulse"
                style={{
                  borderColor: theme === 'light' ? 'var(--surface-hover)' : 'var(--bg)',
                }}
              />
            </div>

            {/* Tooltip com nome — abaixo do avatar */}
            {hoverId === u.id && (
              <div className="avatar-tooltip" style={{ zIndex: 200 }}>{u.nome}</div>
            )}
          </div>
        ))}

        {/* Overflow "+N" */}
        {extras > 0 && (
          <div
            className="relative w-8 h-8 rounded-full grid place-items-center text-[11px] font-700"
            style={{
              background:
                theme === 'light'
                  ? 'rgba(69,91,216,0.12)'
                  : 'rgba(255,255,255,0.1)',
              color: theme === 'light' ? 'var(--accent)' : 'var(--text-secondary)',
              boxShadow: extrasShadow(theme),
            }}
          >
            +{extras}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
